/* AHH 2026: one quick search.
   Type two letters in the big search bar under the header and matching medicines appear straight away.
   Pressing Enter or the search button still works exactly as before (it opens the full results page).
   If anything here fails, the plain search bar keeps working. */
(() => {
  'use strict';

  const input = document.getElementById('AhhBarSearch');
  const form = input && input.closest('form');
  if (!input || !form || form.dataset.ahhQuick === 'on') return;
  form.dataset.ahhQuick = 'on';

  const MIN_LETTERS = 2;
  const MAX_ROWS = 6;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const searchPath = form.getAttribute('action') || root + 'search';

  /* ---------- the panel under the search bar ---------- */
  const panel = document.createElement('div');
  panel.className = 'ahh-quick';
  panel.id = 'AhhQuick';
  panel.setAttribute('role', 'listbox');
  panel.setAttribute('aria-label', 'Matching medicines');
  panel.hidden = true;
  form.appendChild(panel);

  const status = document.createElement('p');
  status.className = 'ahh-sr';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  form.appendChild(status);

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-controls', 'AhhQuick');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-autocomplete', 'list');

  const cache = new Map();
  let timer = 0;
  let controller = null;
  let latest = '';
  let active = -1;

  const rows = () => Array.from(panel.querySelectorAll('[data-ahh-quick-row]'));

  const setActive = (index) => {
    const list = rows();
    active = list.length ? (index + list.length) % list.length : -1;
    list.forEach((row, position) => {
      const on = position === active;
      row.classList.toggle('is-active', on);
      row.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (active >= 0) {
      input.setAttribute('aria-activedescendant', list[active].id);
      list[active].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const close = () => {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };

  const open = () => {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const money = (value) => {
    const number = parseFloat(value);
    if (!isFinite(number)) return '';
    const whole = Math.abs(number - Math.round(number)) < 0.005;
    return (
      '₹' +
      number.toLocaleString('en-IN', {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      })
    );
  };

  const priceText = (product) => {
    const low = parseFloat(product.price_min !== undefined ? product.price_min : product.price);
    const high = parseFloat(product.price_max !== undefined ? product.price_max : product.price);
    if (!isFinite(low)) return '';
    return (isFinite(high) && high - low > 0.004 ? 'From ' : '') + money(low);
  };

  const pictureUrl = (product) => {
    const raw = (product.featured_image && product.featured_image.url) || product.image;
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.href);
      url.searchParams.set('width', '120');
      return url.toString();
    } catch (error) {
      return raw;
    }
  };

  const allResultsUrl = (text) => {
    const join = searchPath.indexOf('?') === -1 ? '?' : '&';
    return searchPath + join + 'q=' + encodeURIComponent(text);
  };

  const render = (text, products) => {
    panel.textContent = '';
    active = -1;
    input.removeAttribute('aria-activedescendant');

    if (!products.length) {
      panel.appendChild(el('p', 'ahh-quick__note', 'No medicine found for “' + text + '”. Please check the spelling or try fewer letters.'));
      status.textContent = 'No medicines found.';
    } else {
      products.forEach((product, index) => {
        const row = el('a', 'ahh-quick__row');
        row.href = product.url;
        row.id = 'AhhQuickRow' + index;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', 'false');
        row.setAttribute('data-ahh-quick-row', '');

        const picture = pictureUrl(product);
        const frame = el('span', 'ahh-quick__pic');
        if (picture) {
          const image = document.createElement('img');
          image.src = picture;
          image.alt = '';
          image.width = 52;
          image.height = 52;
          image.loading = 'lazy';
          frame.appendChild(image);
        }
        row.appendChild(frame);

        const words = el('span', 'ahh-quick__words');
        words.appendChild(el('span', 'ahh-quick__title', product.title || ''));
        if (product.vendor) words.appendChild(el('span', 'ahh-quick__brand', product.vendor));
        row.appendChild(words);

        const price = priceText(product);
        const tail = el('span', 'ahh-quick__price', product.available === false ? 'Sold out' : price);
        row.appendChild(tail);

        panel.appendChild(row);
      });
      status.textContent = products.length + (products.length === 1 ? ' medicine found.' : ' medicines found.');
    }

    const all = el('a', 'ahh-quick__all', 'See all results for “' + text + '”');
    all.href = allResultsUrl(text);
    all.id = 'AhhQuickAll';
    all.setAttribute('role', 'option');
    all.setAttribute('aria-selected', 'false');
    all.setAttribute('data-ahh-quick-row', '');
    panel.appendChild(all);
    open();
  };

  const fetchProducts = (text) => {
    const key = text.toLowerCase();
    if (cache.has(key)) return Promise.resolve(cache.get(key));

    if (controller) controller.abort();
    controller = typeof AbortController === 'function' ? new AbortController() : null;

    const params = new URLSearchParams();
    params.set('q', text);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(MAX_ROWS));
    params.set('resources[options][unavailable_products]', 'last');

    return fetch(root + 'search/suggest.json?' + params.toString(), {
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then((response) => {
        if (!response.ok) throw new Error('search');
        return response.json();
      })
      .then((data) => {
        const found =
          (data && data.resources && data.resources.results && data.resources.results.products) || [];
        cache.set(key, found);
        return found;
      });
  };

  const run = () => {
    const text = input.value.trim().replace(/\s+/g, ' ');
    latest = text;
    if (text.length < MIN_LETTERS) {
      if (controller) controller.abort();
      close();
      return;
    }
    fetchProducts(text)
      .then((products) => {
        if (latest !== text || document.activeElement !== input) return;
        render(text, products);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        /* If the quick list cannot load, the normal search still works */
        close();
      });
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(run, 140);
  });

  input.addEventListener('focus', () => {
    /* Coming back to a box that already shows matches: show them again */
    if (panel.childElementCount && input.value.trim().length >= MIN_LETTERS && latest === input.value.trim().replace(/\s+/g, ' ')) {
      open();
    }
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (!panel.hidden) {
        event.preventDefault();
        close();
      }
      return;
    }
    if (panel.hidden) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(active < 0 ? rows().length - 1 : active - 1);
    } else if (event.key === 'Enter' && active >= 0) {
      const row = rows()[active];
      if (row && row.href) {
        event.preventDefault();
        window.location.assign(row.href);
      }
    }
  });

  /* Keep the typing box focused while a row is pressed, so the tap is not lost */
  panel.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') event.preventDefault();
  });

  document.addEventListener('pointerdown', (event) => {
    if (panel.hidden) return;
    const target = event.target;
    if (target instanceof Node && !form.contains(target)) close();
  });

  form.addEventListener('focusout', (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !form.contains(next)) close();
  });

  form.addEventListener('submit', () => {
    window.clearTimeout(timer);
    close();
  });

  window.addEventListener('pageshow', close);

  /* ---------- the "Search" word in the header takes the customer to this same search bar ---------- */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('search-button .header-actions__action, .search-action .header-actions__action');
      if (!button) return;
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!document.body.contains(input) || input.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: calm ? 'auto' : 'smooth' });
      input.focus({ preventScroll: true });
      if (typeof input.select === 'function') input.select();
    },
    true
  );
})();
